import { withAliasKeys } from '@components/reports/aliasedGridColumns'
import { fmtMoney, esc, sectionLabel } from './segmentMatrix'
import { flattenPLAccounts, ROW_TOTAL } from './plAccountTree'

/**
 * The P&L / Comparative P&L statement as a grid report (spec 127 §4.2 / §4.3).
 *
 * MOVED out of `PLReport.jsx`'s `gridReport` memo (spec 127 slice S2) so the page,
 * the queue drawer and the tests build the statement from ONE pure function. The
 * move carries four fixes to numbers the page used to print wrong (CC-53903):
 *
 *  1. **Report-level change = the plain difference of CB's two figures.** The
 *     Net Income Change used to render `report.netIncomeDiff`. CB's report-level
 *     Diff scalars apply a both-negative `|a − b|` rule
 *     (`AccountingModels.cs:2790-2801`, `:2869-2880`), so a GROWING loss reads as
 *     an improvement — measured 4 of 160 live runs, pinned: 09/2024 accrual Prior
 *     Year, Net Income −234,398.61 vs −11,919.18, CB `netIncomeDiff` +222,479.43,
 *     truth −222,479.43. `centsDiff` below subtracts CB's two figures instead —
 *     the same operation CB performs, and was measured to perform exactly, on all
 *     5,449 line diffs and all 632 section diffs of those runs. This computes no
 *     total; line and section changes keep using CB's own (plain) diffs.
 *  2. **"% Change" is never a fabricated 0.** `pctValue` returned 0 for a zero
 *     prior, so while the screen showed "—" every export printed 0.0 (measured:
 *     "Fees DAL/FAM $3,250.00 | $0.00 | $3,250.00 | 0.0"). It now returns null and
 *     the cell carries no `dataValue`, so the export view carries the screen's own
 *     text. The column declares `percent: true, decimals: 1`.
 *  3. **Net Income of exactly 0 is a Net Income.** `netIncome || netIncomeAmount`
 *     turned 0 into undefined and the Net Income row vanished (measured reachable:
 *     the "Today"/"Yesterday" presets return `netIncome: 0` on vladdemo). `??`.
 *  4. **Every total cell carries a `dataValue` and prints $0.00 for zero.** Section
 *     totals and Net Income had none, so a zero total printed blank and a negative
 *     one reached the client XLSX as the text "($x)" (`toNumber` cannot parse
 *     parentheses). Account lines keep blank-for-zero (`fmtMoney`).
 *
 * GUARDRAIL (unchanged by the move): sections map 1:1 to grid groups IN SERVER
 * ORDER, and every figure is CB's. Within a section the account TREE is flattened
 * into V1's row sequence (CC-50249): the account with its OWN `amount`, then its
 * descendants, then a "Total {parent}" line carrying the roll-up. See
 * plAccountTree.js for the reconciliation identity — Σ(account rows' `amount`) ===
 * sectionTotal, so rendering children while a parent still showed `totalAmount`
 * would double count.
 *
 * Spec 127 slice S4 (§4.2 — A1, A2, A3 on screen):
 *
 *  - **Roll-up (A1).** Every row carries the grid's `tree` (§4.1): id `'a:'+accountId`
 *    (never a name or a number — names and numbers repeat tenant-wide, only the id
 *    is unique), the nearest rendered ancestor's id, the depth, and `node` / `close`.
 *    A node is ROLLABLE iff the flattened tree gives it a following "Total {parent}"
 *    row — never CB's `isParent`, which is `false` on a Comparative parent whose
 *    children exist only in the comparison period (measured: Rent 121, 8 children).
 *    Its `rolledCells` are that Total row's cells with the node's own number and
 *    name: CB's `totalAmount` (and `totalComparisonAmount` / `…Diff` / the % derived
 *    from those two) — what V1 shows on collapse. The GRID decides visibility; this
 *    builder only hands it CB's rolled figures. It sums nothing.
 *  - **Refuse, don't guess.** If any account id repeats anywhere in the report (or an
 *    account arrives with no id at all), no row gets a `tree` — a collision would
 *    roll up, and hide, the wrong rows — and `rollupUnavailable` names why, so the
 *    page can say so. Only reported when the statement HAS a parent (a Total line):
 *    with nothing to roll up, nothing is lost. Never observed on vladdemo.
 *  - **Indentation is the grid's**, from `tree.depth`: the page no longer pads the
 *    label with a `<span style="padding-left">`, so exports carry depth as a number
 *    instead of losing it to `stripHtml`.
 *  - **"Account Number" (A2)**, first column: CB's `accountNumber` as TEXT — no
 *    `dataValue`, so it is never comma-formatted, never a number in an export and
 *    never counted by Hide $0; blank when an account has none and on every total
 *    line; duplicates ("6025" on Travel and Entertainment AND its child Airfare) and
 *    segmented numbers ("5500.001.002.001") exactly as sent.
 *  - **CB's statement lines (A3).** Gross Profit and Net Operating Income become
 *    extra lines of their section's footer block (`groupFooterRows`, CC-50050), where
 *    V1 places them: Gross Profit after COGS when there is a COGS section, else after
 *    Income — on a Comparative run ONLY after COGS (V1 :353); Net Operating Income
 *    after Expenses. Their changes are `centsDiff` of CB's two figures, never CB's
 *    `grossProfitDiff` / `netOperatingIncomeDiff` (fix 1 above). Net Income stays
 *    the report footer.
 */

// CC-50291: stable hand-written columnIds double as the rename keys. Spec 127 A2:
// "Account Number" first (Jordan's Q3 — the Balance Sheet's CB header, V1's word).
export const PL_COLUMNS = withAliasKeys([
  { columnId: 'accountNumber', label: 'Account Number', visible: true, columnAlignment: 'LEFT' },
  { columnId: 'account', label: 'Account', visible: true, columnAlignment: 'LEFT' },
  { columnId: 'amount', label: 'Amount', visible: true, columnAlignment: 'RIGHT' },
])

/** The grid column that carries the roll-up toggle and the indent. */
export const PL_TREE_COLUMN = 'account'

/**
 * CB's `ProfitLossSectionNames` (V1 `Enumerators.js:1087`, keyed on `section.Order`
 * by V1 at `AccountingPAndLReportView.js:386-391, 626-669`).
 */
export const PL_SECTION_ORDER = { INCOME: 1, EXPENSES: 3, COGS: 6, OTHER_EXPENSES: 7, OTHER_INCOME: 8 }

/** Why a statement offers no roll-up (the page words it, §8). */
export const ROLLUP_UNAVAILABLE = {
  DUPLICATE_ACCOUNT: 'duplicate-account',
  MISSING_ACCOUNT_ID: 'missing-account-id',
}

/**
 * Spec 127 §8: the words a page puts after "Roll-up is unavailable for this run: ".
 * Shared by every statement that refuses a tree through `treeRefusal` — the P&L and,
 * since spec 143, GL Budget vs Actual — so the two can never word one refusal two ways.
 */
export const ROLLUP_UNAVAILABLE_REASONS = {
  [ROLLUP_UNAVAILABLE.DUPLICATE_ACCOUNT]: 'CB sent the same account more than once.',
  [ROLLUP_UNAVAILABLE.MISSING_ACCOUNT_ID]: "CB's account structure could not be read.",
}

export const COMPARE_LABELS = {
  PreviousYear: 'Prior Year',
  PreviousPeriod: 'Prior Period',
  YearToDate: 'Year to Date',
}

export const comparisonColumns = (compare) => [
  ...PL_COLUMNS,
  // CC-50291: the compare column's LABEL is dynamic (it names the compare
  // period) but its identity is not, so the rename key is 'prior' either way.
  ...withAliasKeys([
    { columnId: 'prior', label: COMPARE_LABELS[compare] || 'Prior', visible: true, columnAlignment: 'RIGHT' },
    { columnId: 'change', label: 'Change', visible: true, columnAlignment: 'RIGHT' },
    // Spec 127 §4.3 (5): a column must DECLARE it is a percentage (nothing is
    // auto-detected); the values are points, e.g. -88.6 for "-88.6%".
    { columnId: 'pct', label: '% Change', visible: true, columnAlignment: 'RIGHT', percent: true, decimals: 1 },
  ]),
]

/**
 * % of change = diff / |prior|, derived from the two SERVER figures (never an
 * aggregate). No prior-period baseline → no meaningful percentage → em dash when
 * something changed, blank when nothing did.
 */
export const fmtPct = (diff, prior) => {
  const p = Number(prior ?? 0)
  const d = Number(diff ?? 0)
  if (!p) return d ? '—' : ''
  return `${((d / Math.abs(p)) * 100).toFixed(1)}%`
}

/** The % as a number of points, or null when the prior is 0 — never a fabricated 0. */
export const pctValue = (diff, prior) => {
  const p = Number(prior ?? 0)
  return p ? (Number(diff ?? 0) / Math.abs(p)) * 100 : null
}

/** A finite number from a CB figure, or null when it is absent/unparseable. */
const figure = (v) => {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * Money on a TOTAL line: like `fmtMoney`, but zero prints "$0.00" — V1 prints
 * "Total Other Income $0.00", and a blank total reads as missing. An ABSENT figure
 * stays blank: a missing value printed as $0.00 would be a fabricated zero.
 */
export const fmtTotal = (v) => {
  const n = figure(v)
  if (n == null) return ''
  return n === 0 ? '$0.00' : fmtMoney(n)
}

/**
 * current − prior, to the cent, from CB's own two figures (spec 127 §4.3 (4)) —
 * `(round(a·100) − round(b·100)) / 100`. Null when either figure is absent, so a
 * missing prior can never become a change equal to the whole current figure.
 * Used for the report-level lines (Net Income, Net Operating Income and Gross
 * Profit), NEVER CB's `netIncomeDiff` / `netOperatingIncomeDiff` / `grossProfitDiff`.
 */
export const centsDiff = (current, prior) => {
  const a = figure(current)
  const b = figure(prior)
  if (a == null || b == null) return null
  return (Math.round(a * 100) - Math.round(b * 100)) / 100
}

/** The report-level Net Income, 0 included (spec 127 §4.3 (6): `??`, never `||`). */
export const netIncomeOf = (report) => report?.netIncome ?? report?.netIncomeAmount

const pctCell = (diff, prior) => {
  const v = pctValue(diff, prior)
  return { columnId: 'pct', displayValue: esc(fmtPct(diff, prior)), ...(v != null ? { dataValue: v } : {}) }
}

const totalMoneyCell = (columnId, v) => {
  const n = figure(v)
  return { columnId, displayValue: fmtTotal(v), ...(n != null ? { dataValue: n } : {}) }
}

// Comparison cells for an ACCOUNT line — all values from the server payload.
const accountCompareCells = (prior, diff) => [
  { columnId: 'prior', displayValue: fmtMoney(prior), dataValue: Number(prior || 0) },
  { columnId: 'change', displayValue: fmtMoney(diff), dataValue: Number(diff || 0) },
  pctCell(diff, prior),
]

// Comparison cells for a TOTAL line ("Total {parent}", section totals, Net Income).
const totalCompareCells = (prior, diff) => [
  totalMoneyCell('prior', prior),
  totalMoneyCell('change', diff),
  pctCell(diff, prior),
]

/** A section's `order` (CB's ProfitLossSectionNames value), from the flattened
 *  field or the `sectionName` struct. */
const sectionOrderOf = (sec) => Number(sec?.order ?? sec?.sectionName?.order)

/** An account id cb2 can key a tree row on: present and not blank. */
const usableAccountId = (id) => id != null && String(id).trim() !== ''

/**
 * Can this statement's rows carry a tree? Every ACCOUNT row (Total lines share
 * their account's id by construction) must have its own id, used once in the whole
 * report. Returns the refusal reason, or null. Exported for GL Budget vs Actual,
 * which flattens the same account tree (spec 143).
 */
export const treeRefusal = (flats) => {
  const seen = new Set()
  let duplicate = false
  for (const flat of flats) {
    for (const r of flat) {
      if (r.kind === ROW_TOTAL) continue
      if (!usableAccountId(r.item.accountId)) return ROLLUP_UNAVAILABLE.MISSING_ACCOUNT_ID
      const key = String(r.item.accountId).trim()
      if (seen.has(key)) duplicate = true
      seen.add(key)
    }
  }
  return duplicate ? ROLLUP_UNAVAILABLE.DUPLICATE_ACCOUNT : null
}

export const treeId = (accountId) => `a:${String(accountId).trim()}`

/**
 * One statement line CB computes at report level (Gross Profit, Net Operating
 * Income), as an extra line of a section's footer block. The change is the plain
 * difference of CB's two figures (`centsDiff`) — never CB's `*Diff` scalar, which
 * reports a growing loss as an improvement. `blankComparison` leaves the prior /
 * change / % cells empty (a figure CB is known to get wrong, see Gross Profit).
 * Null when CB sent no figure: an absent line is never printed as $0.00.
 */
const statementLine = (label, current, prior, { comparisonActive, blankComparison = false }) => {
  if (figure(current) == null) return null
  const cells = [{ columnId: 'account', displayValue: esc(label) }, totalMoneyCell('amount', current)]
  if (comparisonActive) {
    if (blankComparison) {
      cells.push(
        { columnId: 'prior', displayValue: '' },
        { columnId: 'change', displayValue: '' },
        { columnId: 'pct', displayValue: '' },
      )
    } else {
      cells.push(...totalCompareCells(prior, centsDiff(current, prior)))
    }
  }
  return cells
}

/**
 * @param {object} report the P&L payload (`data.report`)
 * @param {object} opts
 * @param {boolean} opts.comparisonActive the DATA was run with a compare period
 * @param {string}  opts.ranCompare       that compare period's enum NAME (column label)
 * @returns {{columns: Array, reportData: Array, reportFooterCells: Array,
 *   treeColumnId: string, rollupUnavailable?: string}}
 */
export function buildPlStatementReport(report, { comparisonActive = false, ranCompare } = {}) {
  const sections = report?.sections || []
  const netIncome = netIncomeOf(report)
  const flats = sections.map((sec) => flattenPLAccounts(sec.lineItems))
  // The duplicate-id guard (§4.2): a collision would roll up — and hide — the
  // wrong rows, so a statement with one carries no tree at all.
  const refusal = treeRefusal(flats)
  const withTree = refusal == null
  const hasParent = flats.some((flat) => flat.some((r) => r.kind === ROW_TOTAL))

  // A3: where V1 prints CB's report-level lines (keyed on section order, V1
  // :386-391, :626-669; Comparative :353-388).
  const hasCogs = sections.some((sec) => sectionOrderOf(sec) === PL_SECTION_ORDER.COGS)
  const grossProfitAfter = hasCogs ? PL_SECTION_ORDER.COGS : (comparisonActive ? null : PL_SECTION_ORDER.INCOME)
  const grossProfitLine = () => statementLine('Gross Profit', report?.grossProfit, report?.grossComparisonProfit, {
    comparisonActive,
    // CB's grossComparisonProfit is 0 whenever the comparison period has no COGS
    // (AccountingModels.cs:2908-2917, spec 127 §2.2 / B3), so a change beside it
    // would be all of Gross Profit. Left blank rather than printed.
    // (An absent figure is treated the same: never a change against a guess.)
    blankComparison: !figure(report?.totalComparisonCOGS),
  })
  const netOperatingIncomeLine = () => statementLine(
    'Net Operating Income', report?.netOperatingIncome, report?.netOperatingComparisonIncome, { comparisonActive }
  )

  const reportData = sections.map((sec, si) => {
    const label = sectionLabel(sec)
    const flat = flats[si]
    // Rollable = the flattened tree gave the account a following Total line —
    // keyed on the item itself, never on CB's `isParent`.
    const closes = new Set(flat.filter((r) => r.kind === ROW_TOTAL).map((r) => r.item))
    const group = {
      isGroup: true,
      groupHeaderContent: esc(label),
      dataRows: flat.map(({ item: li, depth, kind, parentId }) => {
        const isTotal = kind === ROW_TOTAL
        const name = esc(li.accountName || li.name || '')
        // A2: CB's number as text; blank on every Total line.
        const numberCell = () => ({ columnId: 'accountNumber', displayValue: isTotal ? '' : esc(li.accountNumber ?? '') })
        const row = {
          isGroup: false,
          dataCells: [
            numberCell(),
            {
              columnId: 'account',
              displayValue: isTotal ? `<span class="font-medium">Total ${name}</span>` : name,
            },
            // An account line shows its OWN amount; only the roll-up line
            // shows totalAmount. Never both for one account.
            isTotal
              ? totalMoneyCell('amount', li.totalAmount)
              : { columnId: 'amount', displayValue: fmtMoney(li.amount), dataValue: Number(li.amount || 0) },
            ...(comparisonActive
              ? (isTotal
                  ? totalCompareCells(li.totalComparisonAmount, li.totalComparisonAmountDiff)
                  : accountCompareCells(li.comparisonAmount, li.comparisonAmountDiff))
              : []),
          ],
        }
        if (withTree) {
          const tree = {
            id: treeId(li.accountId),
            parentId: parentId != null ? treeId(parentId) : null,
            depth,
            kind: isTotal ? 'close' : 'node',
          }
          // A rolled parent shows what its Total line shows — CB's rolled figures —
          // under its own number and name (V1's collapsed row).
          if (!isTotal && closes.has(li)) {
            tree.rolledCells = [
              numberCell(),
              { columnId: 'account', displayValue: name },
              totalMoneyCell('amount', li.totalAmount),
              ...(comparisonActive ? totalCompareCells(li.totalComparisonAmount, li.totalComparisonAmountDiff) : []),
            ]
          }
          row.tree = tree
        }
        return row
      }),
    }
    // Per-section "Total {name}" — server-provided totals pass through, and the
    // section change is CB's own `comparisonAmountSectionTotalDiff` (plain on
    // 632 / 632 measured sections).
    const lines = []
    if (sec.sectionTotal != null) {
      lines.push([
        { columnId: 'account', displayValue: `Total ${esc(label)}` },
        totalMoneyCell('amount', sec.sectionTotal),
        ...(comparisonActive
          ? totalCompareCells(sec.comparisonAmountSectionTotal, sec.comparisonAmountSectionTotalDiff)
          : []),
      ])
    }
    const order = sectionOrderOf(sec)
    const extra = order === grossProfitAfter
      ? grossProfitLine()
      : order === PL_SECTION_ORDER.EXPENSES ? netOperatingIncomeLine() : null
    if (extra) lines.push(extra)
    if (lines.length > 0) group.groupFooterCells = lines[0]
    // CC-50050's multi-line block — only when there IS a second line, so a
    // section with just its total keeps exactly the shape it always had.
    if (lines.length > 1) group.groupFooterRows = lines
    return group
  })

  // Net Income → report footer, color semantics kept via escaped span. The change
  // is the plain difference of CB's two Net Income figures — never
  // `report.netIncomeDiff` (see the file header, fix 1).
  const reportFooterCells = netIncome != null
    ? [
        { columnId: 'account', displayValue: 'Net Income' },
        {
          ...totalMoneyCell('amount', netIncome),
          displayValue: `<span class="${Number(netIncome) >= 0 ? 'text-cb-teal-dark' : 'text-red-600'}">${fmtTotal(netIncome)}</span>`,
        },
        ...(comparisonActive
          ? totalCompareCells(report?.netComparisonIncome, centsDiff(netIncome, report?.netComparisonIncome))
          : []),
      ]
    : []

  return {
    columns: comparisonActive ? comparisonColumns(ranCompare) : PL_COLUMNS,
    reportData,
    reportFooterCells,
    treeColumnId: PL_TREE_COLUMN,
    // Said only when something WOULD have rolled up: with no parent nothing is lost.
    ...(refusal && hasParent ? { rollupUnavailable: refusal } : {}),
  }
}

export default buildPlStatementReport
